import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  Renderer2,
  ChangeDetectorRef,
  HostListener,
  ElementRef,
  NgZone
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, of } from 'rxjs';

import { DOCUMENT } from '@angular/common';
import { DirectionService } from '../direction.service';
import { AuthService } from '../auth/auth.service';
import { Router, NavigationEnd } from '@angular/router';
import { DialogService } from '../dialog/dialog.service';

import { UsersService } from '../super-management/user/users.service';
import { OrdersService } from '../other-pages/my-orders/orders-service';

import { delay, switchMap, filter } from 'rxjs/operators';
import { BranchesService } from 'src/app/super-management/branch/branches.service';
import { set } from 'lodash';

import { MatSnackBar } from '@angular/material/snack-bar';
import { DataSharingService } from '../main-section/data-shering-service/data-sharing.service';

@Component({
  selector: 'app-main-nav',
  templateUrl: './main-nav.component.html',
  styleUrls: ['./main-nav.component.scss'],
  host: {
    class: 'fill-screen',
  },
})
export class MainNavComponent implements OnInit, OnDestroy {
  isDarkMode: boolean = false;
  userIsAuthenticated = false;
  private authListenerSubs: Subscription;
  isDrawerOpen: boolean = false;
  isProManuOpen: boolean = false;
  isRTL: boolean = true;
  selectedLanguage: string = 'he';
  public selectedTheme: string = 'light';
  public isDarkTheme: boolean = false;
  private directionSubscription: Subscription;
  tooltipContentMode: string = '';
  tooltipContentLanguage: string = '';
  roles: string[] = [];
  private rolesSubscription: Subscription;
  userId: string;
  isSU: boolean = false;
  /** יש bmph ואין su – תפריט ניהול כמו תלת־מימד, תמיד ניווט ל־/printer/ph */
  isBmphOnly: boolean = false;
  userName = '';
  private userNameSubscription: Subscription;
  greeting = '';
  private numOfOrdersSub: Subscription;
  private numOfPointsSub: Subscription;
  numOfPendingOrders: number = 0;
  numOfPoints: number = 0;
  private intervalId: any;
  user: any = {};
  userProfileImg: string;
  isRootScreen = false;
  private defaultProfileUrl = '../../assets/images/profile-default.svg';
  isLoggedOutLoading: boolean = false;

  // ph
  isPH: boolean = false;

  // Keyboard shortcuts for SU
  private isWaitingForNumber: boolean = false;
  private keyboardShortcutTimeout: any = null;
  
  // Long press handling for hamburger menu
  private hamburgerPressTimer: any = null;
  private hamburgerPressStartTime: number = 0;
  private hamburgerLongPressTriggered: boolean = false;
  private readonly LONG_PRESS_DURATION: number = 800; // 1.5 seconds for long press

  constructor(
    public translateService: TranslateService,
    private directionService: DirectionService,
    @Inject(DOCUMENT) private document: Document,
    private authService: AuthService,
    private router: Router,
    private render: Renderer2,
    private usersService: UsersService,
    private dialogService: DialogService,
    private cd: ChangeDetectorRef,
    private ordersService: OrdersService,
    private elementRef: ElementRef,
    private branchesService: BranchesService,
    private snackBar: MatSnackBar,
    private dataSharingService: DataSharingService

  ) {
    this.translateService.onLangChange.subscribe(() => {
      this.updateTranslation();
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isRootScreen = event.urlAfterRedirects.startsWith('/screen');
      }
    });
  }

  /** When true, we're on the monitor view – only the top mat-toolbar is hidden (not the whole nav). */
  get isMonitorRoute(): boolean {
    const u = this.router.url;
    return u === '/monitor' || u.startsWith('/monitor?');
  }

  /** When true, we're on the business management view – hide navbar like monitor. */
  get isBusinessRoute(): boolean {
    const u = this.router.url;
    return u === '/business' || u.startsWith('/business?');
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    // Only for SU users
    if (!this.isSU) {
      return;
    }

    // Don't trigger shortcuts if user is typing in an input field
    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) {
      return;
    }

    // Check if 'a' or 'A' is pressed (using event.code to work with any keyboard layout)
    // event.code is 'KeyA' for the physical 'a' key regardless of keyboard layout
    if (event.code === 'KeyA' || event.key === 'a' || event.key === 'A') {
      // Prevent default behavior
      event.preventDefault();
      
      // If we're already waiting, reset
      if (this.isWaitingForNumber) {
        this.clearKeyboardShortcutTimeout();
      }
      
      // Set waiting state
      this.isWaitingForNumber = true;
      
      // Set timeout to clear waiting state after 2 seconds
      this.keyboardShortcutTimeout = setTimeout(() => {
        this.isWaitingForNumber = false;
        this.keyboardShortcutTimeout = null;
      }, 2000);
      
      return;
    }

    // If we're waiting for a number and a number 1-6 is pressed
    if (this.isWaitingForNumber && event.key >= '1' && event.key <= '6') {
      event.preventDefault();
      this.clearKeyboardShortcutTimeout();
      this.isWaitingForNumber = false;

      // Map numbers to pages
      const pageMap: { [key: string]: string } = {
        '1': 'printer',
        '2': 'orders',
        '3': 'users',
        '4': 'inventory',
        '5': 'branch',
        '6': 'report'
      };

      const page = pageMap[event.key];
      if (page) {
        // Check if right place dialog is open, and close it first
        if (this.dialogService.isRightPlaceDialogOpen()) {
          this.dialogService.onCloseRightPlaceDialog();
          // Wait a bit for the dialog to close before navigating
          setTimeout(() => {
            this.goToManagementPage(page);
          }, 100);
        } else {
          this.goToManagementPage(page);
        }
      }
    } else if (this.isWaitingForNumber && (event.key === 'q' || event.key === 'Q' || event.code === 'KeyQ')) {
      // Handle A + Q: אם כבר במוניטור – מעבר ל־/; אחרת – מעבר למוניטור (רק ל־SU)
      event.preventDefault();
      this.clearKeyboardShortcutTimeout();
      this.isWaitingForNumber = false;
      if (this.isMonitorRoute) {
        this.closeDrawer();
        this.closeProfileManu();
        this.router.navigate(['/']);
      } else {
        this.goToMonitor();
      }
    } else if (this.isWaitingForNumber) {
      // If waiting but wrong key pressed, clear waiting state
      this.clearKeyboardShortcutTimeout();
      this.isWaitingForNumber = false;
    }
  }

  private clearKeyboardShortcutTimeout(): void {
    if (this.keyboardShortcutTimeout) {
      clearTimeout(this.keyboardShortcutTimeout);
      this.keyboardShortcutTimeout = null;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.closeProfileManu();
    }
  }

  ngOnInit() {
    // localStorage.removeItem("printingService");
    // localStorage.removeItem("branch");
    this.userIsAuthenticated = this.authService.getIsAuth();
    if (this.userIsAuthenticated) {
      this.getOrders();
    } else {
      const now = new Date().getTime();
      const expiration = localStorage.getItem('expiration');
      if (expiration) {
        const expirationTime = new Date(expiration).getTime();
        if (now > expirationTime) {
          this.authService.logout();
          this.dialogService.onOpenLoginDialog();
        }
      }
    }
    this.authListenerSubs = this.authService
      .getAuthStatusListener()
      .subscribe((isAuthenticated) => {
        this.userIsAuthenticated = isAuthenticated;
        this.userId = this.authService.getUserId();
        if (this.userIsAuthenticated) {
          this.getOrders();
        } else {
          this.numOfPendingOrders = 0;
          this.numOfPoints = 0;
          if (this.intervalId) {
            clearInterval(this.intervalId);
          }
        }
        this.updateUser();
      });

    // Subscribe to the authCompleted event
    this.authService.getAuthCompletedListener().subscribe(() => {
      this.getOrders();
    });

    this.authService.userUpdated$.subscribe((user) => {
      this.user = user;
      this.userProfileImg = this.getUserProfileImg(user);
    });

    this.directionSubscription = this.directionService.direction$.subscribe(
      (direction) => {
        this.isRTL = direction === 'rtl';
      }
    );

    this.dataSharingService.getPendingOrderIncrementListener().subscribe(() => {
      this.numOfPendingOrders++;
    });

    this.dataSharingService.getPendingOrderDecrementListener().subscribe(() => {
      if (this.numOfPendingOrders > 0) {
        this.numOfPendingOrders--;
      }
    });

    this.directionService.isDarkMode$.subscribe((isDarkMode) => {
      this.isDarkMode = isDarkMode;
    });

    this.directionService.currentLanguage$.subscribe((lang) => {
      // console.log("selected---Lang---uage: 6" + lang, this.selectedLanguage)
      if (lang !== this.selectedLanguage) {
        this.selectedLanguage = lang;
      }
    });

    this.rolesSubscription = this.authService.roles$.subscribe((roles) => {
      this.roles = roles ?? [];
      this.isSU = this.roles.includes('su');
      this.isBmphOnly = this.roles.includes('bmph') && !this.roles.includes('su');
      this.cd.detectChanges();
    });

    this.userNameSubscription = this.authService.userName$.subscribe(
      (userName) => {
        this.userName = userName;
        this.cd.detectChanges();
      }
    );

    this.updateGreeting();
    setInterval(() => {
      this.updateGreeting();
    }, 60000);

    this.updateUser();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (event.urlAfterRedirects === '/home' && this.isIphone()) {
          this.render.addClass(
            this.elementRef.nativeElement,
            'fill-screen-home'
          );
        } else {
          this.render.removeClass(
            this.elementRef.nativeElement,
            'fill-screen-home'
          );
        }
      });

    this.checkInternetConnection();
  }

  // update user
  updateUser() {
    if (localStorage.getItem('userId')) {
      this.usersService
        .getUser(localStorage.getItem('userId'))
        .subscribe((user) => {
          if (user) {
            this.user = user;
            if (this.user.isDarkMode) {
              this.changeTheme('dark');
            } else {
              this.changeTheme('light');
            }
          }
        });
    }
  }

  getOrders() {
    // get orders [init]
    this.ordersService.getNumOfPendingOrders();
    this.numOfOrdersSub = this.ordersService
      .getNumOfPendingOrdersUpdateListener()
      .subscribe((orderData: { numOfPendingOrders: number }) => {
        this.numOfPendingOrders = orderData.numOfPendingOrders;
      });
    this.numOfPointsSub = this.ordersService
      .getNumOfPointsUpdateListener()
      .subscribe((orderData: { numOfPoints: number }) => {
        this.numOfPoints = orderData.numOfPoints;
      });
    // get orders [interval]
    this.intervalId = setInterval(() => {
      this.ordersService.getNumOfPendingOrders();
    }, 30000);
  }

  onLogout() {
    this.isProManuOpen = false;
    this.isLoggedOutLoading = true;
    this.authService.logout().then(() => {
      // ... after logging out finished
      this.isLoggedOutLoading = false;
      this.router.navigate['/'];
    });
  }

  ngOnDestroy() {
    this.authListenerSubs.unsubscribe();
    if (this.directionSubscription) {
      this.directionSubscription.unsubscribe();
    }
    this.rolesSubscription.unsubscribe();
    this.clearKeyboardShortcutTimeout();
  }

  // Drawer
  onHamburgerClick(event: MouseEvent): void {
    // For SU users, prevent default click behavior since we handle it in pressEnd
    if (this.isSU) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    // For non-SU users, allow normal drawer toggle
    this.toggleDrawer();
  }

  toggleDrawer() {
    this.isDrawerOpen = !this.isDrawerOpen;
    if (this.isDrawerOpen) {
      this.isPH =
        localStorage.getItem('printingService') === 'ph' || this.isBmphOnly;
    }
  }

  onHamburgerPressStart(event: MouseEvent | TouchEvent): void {
    // Only for SU users
    if (!this.isSU) {
      return;
    }
    
    // Prevent default to avoid context menu on long press
    event.preventDefault();
    event.stopPropagation();
    
    this.hamburgerPressStartTime = Date.now();
    this.hamburgerLongPressTriggered = false;

    // Set timer for long press
    this.hamburgerPressTimer = setTimeout(() => {
      this.hamburgerLongPressTriggered = true;
      this.goToMonitor();
      this.hamburgerPressTimer = null;
    }, this.LONG_PRESS_DURATION);
  }

  onHamburgerPressEnd(event: MouseEvent | TouchEvent): void {
    // Only for SU users
    if (!this.isSU) {
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();
    
    if (this.hamburgerPressTimer) {
      clearTimeout(this.hamburgerPressTimer);
      this.hamburgerPressTimer = null;
      
      // If press duration was less than long press threshold, open drawer
      const pressDuration = Date.now() - this.hamburgerPressStartTime;
      if (pressDuration < this.LONG_PRESS_DURATION && !this.hamburgerLongPressTriggered) {
        this.toggleDrawer();
      }
    }
    
    // Reset the flag after handling
    this.hamburgerLongPressTriggered = false;
  }

  openDrawer() {
    this.isDrawerOpen = true;
  }

  closeDrawer() {
    this.isDrawerOpen = false;
  }

  // Profile Manu
  toggleProfileManu() {
    this.isProManuOpen = !this.isProManuOpen;
    // console.log('this.isProManuOpen:', this.isProManuOpen);
  }

  closeProfileManu() {
    this.isProManuOpen = false;
  }

  // settings
  changeTheme(themeValue: string) {
    if (themeValue !== this.selectedTheme) {
      this.selectedTheme = themeValue;
      this.render.removeClass(this.document.body, 'lightTheme');
      this.render.removeClass(this.document.body, 'darkTheme');
      this.render.addClass(this.document.body, themeValue + 'Theme');
      if (this.isDarkTheme) {
        this.closeProfileManu();
        this.isDarkTheme = false;
        this.toggleDarkMode(false);
      } else {
        this.closeProfileManu();
        this.isDarkTheme = true;
        this.toggleDarkMode(true);
      }
    }
  }

  goToLanguage(lang) {
    if (lang !== this.selectedLanguage) {
      this.directionService.toLanguageDirection(lang);
      if (this.userIsAuthenticated) {
        this.usersService.updateUserLanguage(lang);
        this.closeProfileManu();
      }
    }
  }

  toggleDarkMode(isDarkMode: boolean) {
    this.directionService.setDarkMode(isDarkMode);
    this.closeProfileManu();
    if (this.userIsAuthenticated) {
      this.usersService.updateUserMode(isDarkMode);
    }
  }

  goToHome() {
    this.closeDrawer();
    this.closeProfileManu();
    const current = this.router.url.split('?')[0];
    if (current !== '/' && current !== '') {
      this.router.navigate(['/']);
    }
  }

  goToAboutUs() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate(['/home']);
  }

  goToBusiness() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate(['/joinus']);
  }

  updateTranslation() {
    this.tooltipContentMode = this.translateService.instant(
      'main-nav.tooltip-mode'
    );
    this.tooltipContentLanguage = this.translateService.instant(
      'main-nav.tooltip-language'
    );
  }

  openLoginDialog() {
    this.closeDrawer();
    this.closeProfileManu();
    this.dialogService.onOpenLoginDialog('', '');
  }

  goToTandC() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate(['/tandc']);
  }

  goToPP() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate(['/pp']);
  }

  goToBranchList() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate(['/branchlist']);
  }

  goToUserList() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate(['/userlist']);
  }

  goToPaperList() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate(['/paperlist']);
  }

  openWhatsApp() {
    const phoneNumber = '97233746962';
    const message = encodeURIComponent(
      'Hi Eazix, I need some help printing :)'
    );
    const url = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(url, '_blank');
    this.closeDrawer();
    this.closeProfileManu();
  }

  goToNewOrder() {
    this.closeDrawer();
    this.closeProfileManu();
    const printingService = localStorage.getItem('printingService');
    const branch = localStorage.getItem('branch');
    if (
      printingService &&
      printingService !== 'null' &&
      printingService !== '' &&
      branch &&
      branch !== 'null' &&
      branch !== ''
    ) {
      this.dialogService.onOpenRightPlaceDialog();
    } else if (branch && branch !== 'null' && branch !== '') {
      this.router.navigate(['/branch']);
    } else {
      const current = this.router.url.split('?')[0];
      if (current !== '/' && current !== '') {
        this.router.navigate(['/']);
      }
    }
  }

  goToMyOrders() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate([`/myorders/${localStorage.getItem('userId')}`]);
  }

  goToMyProfile() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate([`/myprofile/${localStorage.getItem('userId')}`]);
  }

  goToMyProfileCreditMode() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate([
      `/myprofile/${localStorage.getItem('userId')}/credit`,
    ]);
  }

  goTo3DProduct() {
    this.closeDrawer();
    this.closeProfileManu();
    this.dataSharingService.setPhPrintDomain('3d');
    this.router.navigate(['/3dproduct']);
  }

  goToEazixExhibition() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate(['/exhibition']);
  }

  goToMyWall() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate(['/wall']);
  }

  hasUserWall(): boolean {
    return localStorage.getItem('haveWall') === 'true';
  }

  goToEditInstructions() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate(['/edit']);
  }

  goToQAndA() {
    this.closeDrawer();
    this.closeProfileManu();
    this.router.navigate(['/qanda']);
  }

  updateGreeting() {
    const currentHour = new Date().getHours();
    if (currentHour >= 6 && currentHour < 12) {
      this.greeting = 'morning';
    } else if (currentHour >= 12 && currentHour < 16) {
      this.greeting = 'noon';
    } else if (currentHour >= 16 && currentHour < 19) {
      this.greeting = 'afternoon';
    } else if (currentHour >= 19 && currentHour < 21) {
      this.greeting = 'evening';
    } else {
      this.greeting = 'night';
    }
  }

  /** Provider pictures (Google/Facebook) can expire or be blocked — show the default avatar instead of a broken image. */
  onProfileImgError(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img && !img.src.endsWith('profile-default.svg')) {
      img.src = this.defaultProfileUrl;
    }
  }

  getUserProfileImg(user: any) {
    if (
      user &&
      user.profileImageURL &&
      this.isValidImageUrl(user.profileImageURL)
    ) {
      return user.profileImageURL;
    } else {
      if (user && user.provider) {
        if (user.provider === 'facebook') {
          return user.providerData?.id
            ? 'https://graph.facebook.com/' +
            user.providerData.id +
            '/picture?type=large'
            : this.defaultProfileUrl;
        } else if (user.provider === 'google') {
          return user.providerData?.picture
            ? user.providerData.picture
            : this.defaultProfileUrl;
        } else {
          return this.defaultProfileUrl;
        }
      } else {
        return this.defaultProfileUrl;
      }
    }
  }

  isValidImageUrl(url: string): boolean {
    const img = new Image();
    if (img.src && url) {
      img.src = url;
      return img.complete && img.naturalWidth !== 0;
    } else {
      return false;
    }
  }

  isHomePage(): boolean {
    return this.router.url === '/home';
  }

  checkInternetConnection() {
    setTimeout(() => {
      if ('connection' in navigator) {
        const connection = (navigator as any).connection;
        const effectiveType = connection.effectiveType;

        if (
          effectiveType === '2g' ||
          effectiveType === 'slow-2g' ||
          effectiveType === '3g'
        ) {
          // Check if we should show the snackbar based on last update time
          const LAST_WIFI_UPDATE_KEY = 'latWifiUpdateTime';
          const TWO_HOURS_MS = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
          
          const lastUpdateTimeStr = localStorage.getItem(LAST_WIFI_UPDATE_KEY);
          const now = Date.now();
          
          let shouldShowSnackbar = false;
          
          if (!lastUpdateTimeStr) {
            // No previous update time - show snackbar and save current time
            shouldShowSnackbar = true;
            localStorage.setItem(LAST_WIFI_UPDATE_KEY, now.toString());
          } else {
            // Check time difference
            const lastUpdateTime = parseInt(lastUpdateTimeStr, 10);
            const timeDifference = now - lastUpdateTime;
            
            if (timeDifference >= TWO_HOURS_MS) {
              // More than 2 hours passed - show snackbar and update time
              shouldShowSnackbar = true;
              localStorage.setItem(LAST_WIFI_UPDATE_KEY, now.toString());
            }
            // If less than 2 hours - don't show snackbar (shouldShowSnackbar remains false)
          }
          
          if (shouldShowSnackbar) {
            setTimeout(() => {
              this.snackBar.open(
                this.translateService.instant(
                  'printing-table.low-internet-connection'
                ),
                '',
                {
                  duration: 2000,
                  verticalPosition: 'top',
                  panelClass: 'zx-top-snackbar',
                }
              );
            }, 100);
          }
        }
      }
    }, 4000);
  }

  async goToManagementPage(page: string) {
    this.closeDrawer();
    this.closeProfileManu();
    // מנהל תלת־מימד בלבד: קודם מעדכן "מקום אחרון" ל־ph/ph (כמו בחירת סניף), ואז ניווט – שלא יילכדו ב־/printer של מדפסת אחרונה
    if (this.isBmphOnly) {
      const phService = 'ph';
      const phBranch = 'ph';
      this.authService.updateAuthData(phService, phBranch);
      this.dataSharingService.setPrintingService(phService);
      this.dataSharingService.setBranch(phBranch);
      this.dataSharingService.setPhPrintDomain('3d');
      this.isPH = true;
      this.usersService.updateUserPlace(phService, phBranch).subscribe({
        next: () => {
          this.router.navigate(['/printer/ph/ph'], { queryParams: { q: page } });
        },
        error: () => {
          this.router.navigate(['/printer/ph/ph'], { queryParams: { q: page } });
        },
      });
      return;
    }

    const service = localStorage.getItem('printingService');
    const branch = localStorage.getItem('branch');
    this.isPH = localStorage.getItem('printingService') === 'ph';
    if (!service || !branch) {
      return;
    }
    if (!this.isPH) {
      let branchId = '';
      await this.branchesService
        .getBranchByName(service, branch)
        .pipe(
          switchMap((branchData: any) => {
            // console.log('branchData: !!!!!', branchData);
            // console.log('branchData._id: !!!!!', branchData._id);
            if (branchData._id) {
              branchId = branchData._id;
            } else {
              branchId = branchData;
            }
            return of(branchData);
          })
        )
        .subscribe(() => {
          if (branchId && branchId !== '') {
            this.router.navigate(['/printer/' + service + '/' + branchId], {
              queryParams: { q: page },
            });
          }
        });
    } else {
      this.router.navigate(['/printer/ph/ph'], {
        queryParams: { q: page },
      });
    }
  }

  goToMonitor(): void {
    // Only for SU users
    if (!this.isSU) {
      return;
    }
    this.closeDrawer();
    this.closeProfileManu();
    
    // Check if right place dialog is open, and close it first
    if (this.dialogService.isRightPlaceDialogOpen()) {
      this.dialogService.onCloseRightPlaceDialog();
      // Wait a bit for the dialog to close before navigating
      setTimeout(() => {
        this.router.navigate(['/monitor']);
      }, 100);
    } else {
      this.router.navigate(['/monitor']);
    }
  }

  isIphone(): boolean {
    return /iPhone/.test(navigator.userAgent);
  }

  // FONT SIZE
  fontSizes = [
    { label: 'גודל מינימלי', value: 'font-small' },
    { label: 'גודל בינוני', value: 'font-medium' },
    { label: 'גודל גדול', value: 'font-large' },
    { label: 'גודל ענק', value: 'font-xlarge' }
  ];

  selectedFontSize = 'font-small';

  setFontSize(size: string) {
    // הסר את כל מחלקות הפונט
    document.body.classList.remove('font-medium', 'font-large', 'font-xlarge');
    if (size !== 'font-small') {
      document.body.classList.add(size);
    }
    this.selectedFontSize = size;
  }

  // ====================
}
